import { memo, useRef, useEffect, type FC } from 'react';
import { Card } from 'antd';
import { CameraOutlined } from '@ant-design/icons';
import styles from './VideoStreamCard.module.css';
import sampleVideoCounting from '@/assets/sample_osd.webm';
import sampleVideo from '@/assets/sample.webm';
import type { SessionDisplay } from '@smolt/shared/models';

export interface VideoStreamCardProps {
  cameraId: string;
  cameraName: string;
  sessionDisplay: SessionDisplay;
  isCounting?: boolean; // Direct prop for reliable updates
}

/**
 * VideoStreamCard - Counter Page Video Stream Card Component
 * Memoized to prevent unnecessary re-renders when sessionDisplay object identity is unchanged
 */
export const VideoStreamCard: FC<VideoStreamCardProps> = memo(({
  cameraName,
  sessionDisplay,
  isCounting: isCountingProp,
}) => {
  // Use direct prop if provided, otherwise fall back to sessionDisplay
  const actualIsConnected = sessionDisplay.isConnected;
  const actualIsRecording = isCountingProp ?? sessionDisplay.isCounting;

  // Select video source based on counting state
  // Start (counting) -> sampleVideoCounting, Stop (not counting) -> sampleVideo
  const videoSource = actualIsRecording ? sampleVideoCounting : sampleVideo;
  const videoRef = useRef<HTMLVideoElement>(null);

  // Force video to play when source changes
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !actualIsConnected) return;

    // Ensure video plays after source change
    video.play().catch((error) => {
      // Auto-play may be blocked, but that's okay since we have muted attribute
      console.debug('Video autoplay prevented:', error);
    });
  }, [actualIsRecording, actualIsConnected]);

  return (
    <Card
      className={styles.card}
      style={{ height: '100%', border: 'none' }}
      styles={{ body: { padding: 0, height: '100%' } }}
      variant="borderless"
    >
      <div className={styles.videoContainer}>
        {/* Camera Name Overlay */}
        <div className={styles.cameraNameOverlay}>
          {actualIsRecording && <span className={styles.recordingIndicator} />}
          <span className={styles.cameraName}>{cameraName}</span>
        </div>

        {actualIsConnected ? (
          <div className={styles.videoPlaceholder}>
            <video
              ref={videoRef}
              key={videoSource}
              src={videoSource}
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              className={styles.placeholderImage}
            />
          </div>
        ) : (
          <div className={styles.disconnected}>
            <CameraOutlined className={styles.cameraIcon} />
            <p className={styles.disconnectedText}>
              The camera is not connected.
              <br />
              Please check the connection.
            </p>
          </div>
        )}
      </div>
    </Card>
  );
}, (prevProps, nextProps) => {
  // Custom comparison for memo
  const prevIsConnected = prevProps.sessionDisplay.isConnected;
  const nextIsConnected = nextProps.sessionDisplay.isConnected;
  const prevIsRecording = prevProps.isCounting ?? prevProps.sessionDisplay.isCounting;
  const nextIsRecording = nextProps.isCounting ?? nextProps.sessionDisplay.isCounting;

  return (
    prevProps.cameraId === nextProps.cameraId &&
    prevProps.cameraName === nextProps.cameraName &&
    prevIsConnected === nextIsConnected &&
    prevIsRecording === nextIsRecording
  );
});

VideoStreamCard.displayName = 'VideoStreamCard';
